import type { Game, RecordState } from "@e308/core";
import type { ConflictContent } from "./definition.js";
import type { Micro } from "./exact-numbers.js";
import type { MatchTimelineEntry } from "./match-persistence.js";
import type { AuthorityState } from "./types.js";

export function promoteMatchAuthority(
  game: Game<Micro>,
  content: ConflictContent,
  authorityEpoch: number,
): void {
  const current = authorityState(game, content);
  if (!Number.isSafeInteger(authorityEpoch) || authorityEpoch <= current.authorityEpoch)
    throw new TypeError("Recovery authority epoch must advance");
  const result = game.dispatch({
    id: `recovery-authority:${authorityEpoch}`,
    execute: (transaction) => {
      const state = transaction.getRecord(content.authority) as unknown as AuthorityState;
      transaction.setRecord(content.authority, {
        ...state,
        authorityEpoch,
      } as unknown as Parameters<typeof transaction.setRecord>[1]);
    },
  });
  /* v8 ignore next 3 -- definition-owned authority records make this path unreachable */
  if (!result.ok) {
    throw new Error("Could not commit recovery authority epoch");
  }
}

export function transitionMatchController(
  game: Game<Micro>,
  content: ConflictContent,
  timeline: MatchTimelineEntry[],
  seatId: string,
  sessionId: string,
  generation: number,
  mode: "human" | "autopilot",
): void {
  const current = authorityState(game, content);
  if (!content.seatById.has(seatId)) throw new TypeError(`Unknown seat: ${seatId}`);
  if (!sessionId) throw new TypeError("Controller transition requires a session ID");
  if (generation !== requiredControllerGeneration(current, seatId) + 1)
    throw new TypeError("Controller generation must increment exactly once");
  const result = game.dispatch({
    id: `controller:${seatId}:${generation}`,
    execute: (transaction) => {
      const state = transaction.getRecord(content.authority) as unknown as AuthorityState;
      transaction.setRecord(content.authority, {
        ...state,
        controllerGenerations: { ...state.controllerGenerations, [seatId]: generation },
      } as unknown as Parameters<typeof transaction.setRecord>[1]);
      transaction.emit(content.matchLog, {
        kind: "controller-changed",
        seatId,
        sessionId,
        generation,
        mode,
      });
    },
  });
  /* v8 ignore next 3 -- validated transitions cannot fail their internal record command */
  if (!result.ok) {
    throw new Error("Could not commit controller transition");
  }
  timeline.push({
    kind: "controller-transition",
    atGameMs: game.getSnapshot().gameTimeMs,
    seatId,
    sessionId,
    generation,
    mode,
  });
}

export function recordMatchPause(
  game: Game<Micro>,
  content: ConflictContent,
  timeline: MatchTimelineEntry[],
  paused: boolean,
): void {
  const atGameMs = game.getSnapshot().gameTimeMs;
  const result = game.dispatch({
    id: `room-pause:${paused}:${atGameMs}`,
    execute: (transaction) =>
      transaction.emit(content.matchLog, { kind: "room-pause-changed", paused }),
  });
  /* v8 ignore next 3 -- definition-owned lifecycle events have no rejecting operation */
  if (!result.ok) {
    throw new Error("Could not commit room pause transition");
  }
  timeline.push({ kind: "pause-transition", atGameMs, paused });
}

export function matchControllerGeneration(
  game: Game<Micro>,
  content: ConflictContent,
  seatId: string,
): number {
  if (!content.seatById.has(seatId)) throw new TypeError(`Unknown seat: ${seatId}`);
  return requiredControllerGeneration(authorityState(game, content), seatId);
}

function requiredControllerGeneration(state: AuthorityState, seatId: string): number {
  const generation = state.controllerGenerations[seatId];
  if (generation === undefined) throw new TypeError(`Missing controller generation: ${seatId}`);
  return generation;
}

function authorityState(game: Game<Micro>, content: ConflictContent): AuthorityState {
  const state = game.getSnapshot().records?.[content.authority.id] as RecordState;
  return state.value as unknown as AuthorityState;
}
